/**
 * Staff desktop notifications (TRUS-668): polls the notification summary and
 * raises a notification on this computer for every new unread row (a new
 * Inbox message is a `communication` row). Headless; mounted ONCE in the
 * signed-in staff shell, so it runs whatever the sidebar shows (the bell used
 * to do this, and the bell is not rendered while the sidebar is collapsed).
 *
 *  - Every 15 s; on failures it backs off (doubling, up to 5 minutes).
 *  - Keeps its schedule in a background tab (lib/backgroundTimer) and polls the
 *    moment the window becomes visible, focused, or back online.
 *  - New = unread id above the highest already seen (lib/notificationSummaryStore).
 *    Nothing on the first poll of a page: what was unread before is not news.
 *  - Shown through the service worker when web push is on, so the push and
 *    this collapse into ONE notification (lib/browserNotifications); otherwise
 *    the Web Notifications API, or the desktop app's native notifications.
 *  - The rules of lib/desktopMessageNotifications: on unless switched off, and
 *    not for the conversation on screen in a focused window.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import { desktopNotifications } from '@/services/desktopNotifications';
import { setDockBadge } from '@/lib/desktopBadge';
import {
  armDesktopNotificationClick,
  isTauriApp,
  isWindowInBackground,
  messageNotificationsEnabled,
  shouldNotifyForLink,
} from '@/lib/desktopMessageNotifications';
import { PUSH_NAVIGATE_TYPE, parsePushTarget, routeStaffPushTarget } from '@/lib/pushNavigation';
import {
  listenForPushShown,
  messageIdFromLink,
  messageTag,
  notificationRowTag,
  showCollapsingNotification,
} from '@/lib/browserNotifications';
import { backgroundTimeout } from '@/lib/backgroundTimer';
import {
  highestUnreadId,
  newNotificationsSince,
  recordBellOutcome,
  refreshNotificationSummary,
  registerSummaryRefresher,
  setNotificationSummary,
  type BellOutcome,
  type StaffNotification,
  type StaffNotificationSummary,
} from '@/lib/notificationSummaryStore';

export const SUMMARY_POLL_MS = 15_000;
export const SUMMARY_MAX_BACKOFF_MS = 300_000;

/** Open a notification's link in-app when the shell can; otherwise navigate. */
export function openNotificationLink(link: string, onNavigate?: (tab: string) => void) {
  const target = parsePushTarget(link, window.location.origin);
  if (target && onNavigate && routeStaffPushTarget(target, { navigateTab: onNavigate })) return;
  let targetPath = link;
  try {
    const url = new URL(link, window.location.origin);
    targetPath = url.pathname + url.search;
  } catch {
    targetPath = link;
  }
  window.location.href = targetPath;
}

/** The delay before the next poll after `failures` failures in a row. */
export function nextPollDelay(failures: number): number {
  if (failures <= 0) return SUMMARY_POLL_MS;
  return Math.min(SUMMARY_POLL_MS * Math.pow(2, failures), SUMMARY_MAX_BACKOFF_MS);
}

/**
 * Raise one notification row on this computer. Returns what happened, which
 * the Settings self-test reads. `duplicate` only when a push ALREADY showed it
 * in this browser (never a guess), so a push that never arrived cannot
 * silence this one.
 */
export async function raiseNotificationRow(
  row: StaffNotification,
  open: () => void,
): Promise<BellOutcome> {
  const messageId = messageIdFromLink(row.link);
  const collapsed = await showCollapsingNotification({
    title: row.title,
    body: row.message,
    tag: messageId ? messageTag(messageId) : notificationRowTag(row.id),
    url: row.link || '/',
    requireInteraction: row.priority === 'urgent',
    data: {
      type: 'notification',
      notification_id: row.id,
      ...(messageId ? { message_id: messageId } : {}),
    },
  });
  if (collapsed === 'shown') return 'shown';
  if (collapsed === 'duplicate') return 'duplicate';
  await desktopNotifications.showNotificationWithAction(
    {
      title: row.title,
      body: row.message,
      tag: `notification-${row.id}`,
      requireInteraction: row.priority === 'urgent',
      data: { notificationId: row.id, link: row.link },
    },
    () => {
      try {
        window.focus();
      } catch {
        /* ignore */
      }
      open();
    },
  );
  if (desktopNotifications.getPermissionState() !== 'granted') return 'blocked';
  // The desktop app's notifications cannot report a click: the next activation
  // of the window (clicking the notification) stands for it. Only armed while
  // the window is in the background, so someone working in it is never pulled away.
  if (isTauriApp() && isWindowInBackground()) armDesktopNotificationClick(open);
  return 'shown';
}

export function StaffNotificationsHost({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const { user } = useAuth();
  // Latest values for the long-lived poll loop (no stale closures).
  const userRef = useRef(user);
  userRef.current = user;
  const navigateRef = useRef(onNavigate);
  navigateRef.current = onNavigate;

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let cancelTimer: (() => void) | null = null;
    let inFlight: Promise<void> | null = null;
    let failures = 0;
    let lastSeenId: number | null = null; // null until the first poll answered

    const markReadAndOpen = (row: StaffNotification) => () => {
      apiClient
        .patch(`/api/notifications/${row.id}/read`)
        .then(() => poll())
        .catch(() => {});
      if (row.link) openNotificationLink(row.link, navigateRef.current);
    };

    const notifyNew = async (summary: StaffNotificationSummary) => {
      const fresh = newNotificationsSince(summary, lastSeenId ?? 0);
      // Oldest first, so the newest ends up on top.
      for (const row of [...fresh].reverse()) {
        if (cancelled) return;
        if (!messageNotificationsEnabled(userRef.current?.preferences) || !shouldNotifyForLink(row.link)) {
          recordBellOutcome(row.id, 'skipped');
          continue;
        }
        let outcome: BellOutcome;
        try {
          outcome = await raiseNotificationRow(row, markReadAndOpen(row));
        } catch {
          outcome = 'blocked';
        }
        recordBellOutcome(row.id, outcome);
      }
    };

    const fetchOnce = async () => {
      try {
        const response = await apiClient.get('/api/notifications/summary', { params: { counts: 'unread' } });
        if (cancelled) return;
        const summary = response.data as StaffNotificationSummary;
        void setDockBadge(summary.unread_count || 0);
        if (lastSeenId !== null) await notifyNew(summary);
        if (cancelled) return;
        lastSeenId = Math.max(lastSeenId ?? 0, highestUnreadId(summary));
        setNotificationSummary(summary);
        failures = 0;
      } catch (error) {
        failures += 1;
        if (failures <= 3) console.error('Error fetching notification summary:', error);
      }
    };

    // One poll at a time; a poll requested while one runs shares it.
    const poll = (): Promise<void> => {
      if (!inFlight) {
        inFlight = fetchOnce().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    };

    const schedule = () => {
      if (cancelled) return;
      cancelTimer?.();
      cancelTimer = backgroundTimeout(async () => {
        cancelTimer = null;
        if (cancelled) return;
        await poll();
        schedule();
      }, nextPollDelay(failures));
    };

    // Poll at once when the user comes back, and re-arm on every visibility
    // change: a timer armed while visible is a plain page timer, which a hidden
    // tab throttles; one armed while hidden runs in the worker.
    const catchUp = () => {
      if (cancelled) return;
      void poll().then(schedule);
    };

    const unregister = registerSummaryRefresher(poll);
    void poll().then(schedule);
    window.addEventListener('focus', catchUp);
    window.addEventListener('online', catchUp);
    document.addEventListener('visibilitychange', catchUp);

    return () => {
      cancelled = true;
      cancelTimer?.();
      unregister();
      window.removeEventListener('focus', catchUp);
      window.removeEventListener('online', catchUp);
      document.removeEventListener('visibilitychange', catchUp);
    };
    // Keyed on the user's IDENTITY: `user` is a fresh object on many unrelated
    // AuthContext updates, and restarting the loop on each was how poll chains
    // used to pile up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Notifications shown through the service worker: remember what pushes showed
  // here, and mark a clicked one read (the worker routes the click in-app).
  useEffect(() => {
    if (!user || isTauriApp() || typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    listenForPushShown();
    const onMessage = (event: MessageEvent) => {
      const msg = event.data as { type?: string; data?: { notification_id?: unknown } } | null;
      if (!msg || msg.type !== PUSH_NAVIGATE_TYPE) return;
      const id = Number(msg.data?.notification_id);
      if (!Number.isInteger(id) || id <= 0) return;
      apiClient
        .patch(`/api/notifications/${id}/read`)
        .then(() => refreshNotificationSummary())
        .catch(() => {});
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return null;
}

export default StaffNotificationsHost;
